import { useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { faPlus } from '@fortawesome/pro-solid-svg-icons/faPlus'
import { faTags } from '@fortawesome/pro-solid-svg-icons/faTags'
import { IProfile } from 'interfaces/users'
import { lmsProfilesService } from 'api/lms-profiles'
import {
  LmsProfileCourseKind,
  LmsProfileResourceType,
} from 'api/lms-profiles/GetProfilesByResource'
import { ProfileBadge } from 'components/reusables/profileBadge'
import RestrictedResourcesModal from 'components/reusables/restrictedResourcesModal'
import { translationsPrefix } from './contants'

interface IProps {
  resourceId: number | string
  resourceType: LmsProfileResourceType
  close: () => void
  kind?: LmsProfileCourseKind
}

const ProfileModal = ({ resourceId, resourceType, close, kind }: IProps) => {
  const { t } = useTranslation()

  const fetchData = useCallback(
    async ({ page, search, perPage }) => {
      const response = await lmsProfilesService.getProfilesByResource(
        { id: resourceId, type: resourceType },
        { page, search, perPage, kind }
      )

      return {
        allowedItems: response.data.allowedProfiles,
        restrictedCount: response.data.restrictedProfilesCount,
        total: response.data.total,
      }
    },
    [kind, resourceId, resourceType]
  )

  return (
    <RestrictedResourcesModal<IProfile>
      title={t(`${translationsPrefix}.title`)}
      close={close}
      fetchData={fetchData}
      maxWidth={560}
      columns={[
        {
          title: t(`${translationsPrefix}.table.headers.name`),
          key: 'name',
          render: (_name: unknown, profile: IProfile) => (
            <ProfileBadge name={profile.name} color={profile.color} />
          ),
        },
      ]}
      countIcon={faTags}
      addButtonIcon={faPlus}
      locale={{
        rowsCount: t(`${translationsPrefix}.table.rowsCount`),
        rowsCount_plural: t(`${translationsPrefix}.table.rowsCount_plural`),
        search: t('administration.courses.list.search'),
      }}
      closeButtonText={t(`${translationsPrefix}.table.actions.close`)}
      restrictedRowLabel={(count) =>
        t(`${translationsPrefix}.table.restrictedProfile`, { count })
      }
      alertConfig={{
        translationPrefix: translationsPrefix,
        resourceTypeLabel: t(
          `${translationsPrefix}.resourceTypes.${resourceType}`
        ),
        totalItemKey: 'profile',
        restrictedItemKey: 'restrictedProfile',
        totalValueKey: 'totalProfiles',
        restrictedValueKey: 'restrictedProfiles',
      }}
    />
  )
}

export default ProfileModal
